import moment from "moment";

export const dateHelper = {
    addDay(amount, date) {
        return moment(date || undefined).add(amount, 'days');
    },
    addMonth(amount, date) {
        return moment(date || undefined).add(amount, 'months');
    },
    addYear(amount, date) {
        return moment(date || undefined).add(amount, 'years');
    },
    firstDayOfMonth(date) {
        return moment(date || undefined).startOf('month').toDate();
    },
    lastDayOfMonth(date) {
        return moment(date || undefined).endOf('month').toDate();
    },
    isDateEarlierThanNow(date) {
        // Parse the input date string using moment
        const inputDate = moment(date);
        // Check if the input date is before the current date and time
        return inputDate.isBefore(moment(new Date()));
    },
    isSameDate(dateCheck, dateCompare) {
        return moment(dateCheck).isSame(moment(dateCompare), 'day');
    },
    isBeforeDate(dateCheck, dateCompare) {
        return moment(dateCheck).isBefore(moment(dateCompare), 'day');
    },
    isAfterDate(dateCheck, dateCompare) {
        return moment(dateCheck).isAfter(moment(dateCompare), 'day');
    },
    isBetweenDate(dateCheck, startDate, endDate) {
        return moment(dateCheck).isBetween(moment(startDate), moment(endDate), null, '[]') || dateHelper.isSameDate(dateCheck, startDate) || dateHelper.isSameDate(dateCheck, endDate);
    },
    dateTime : {
        default: {
            format(date) {
                return moment(date || undefined).format('YYYY-MM-DD HH:mm:ss');
            },
            display : {
                longFormat (date) {
                    return moment(date || undefined).format('DD MMMM YYYY HH:mm:ss');
                },
                shortFormat (date) {
                    return moment(date || undefined).format('DD MMM YYYY HH:mm:ss');
                },
                timeFormat (date) {
                    return moment(date || undefined).format('HH:mm:ss');
                }
            }
        },
        stringToDate(dateString) {
            // Split the string into date and time components
            const [datePart, timePart] = dateString.split(' ');
            // Split the date component into year, month, and day
            const [year, month, day] = datePart.split('-');
            // Split the time component into hours, minutes, and seconds
            const [hours, minutes, seconds] = timePart.split(':');
            // Create a Date object with the parsed components
            return new Date(year, month - 1, day, hours, minutes, seconds);
        },
        formatString(dateTimeString) {
            const now = moment();
            const targetDateTime = moment(dateTimeString);
            const diffInMinutes = now.diff(targetDateTime, 'minutes');

            if (diffInMinutes < 60) {
                return `${diffInMinutes} minute${diffInMinutes === 1 ? '' : 's'} ago`;
            } else {
                return targetDateTime.format('DD MMM YYYY HH:mm');
            }
        }
    },
    date : {
        default: {
            format(date) {
                return moment(date || undefined).format('YYYY-MM-DD');
            },
            display : {
                longFormat (date) {
                    return moment(date || undefined).format('DD MMMM YYYY');
                },
                shortFormat (date) {
                    return moment(date || undefined).format('DD MMM YYYY');
                },
                timeFormat (date) {
                    return moment(date || undefined).format('HH:mm:ss');
                }
            }
        },
        stringToDate(dateString) {
            // Split the date string into year, month, and day
            const [year, month, day] = dateString.split('-');
            // Create a Date object with the parsed components
            return new Date(year, month - 1, day);
        }
    },
    months(lang= 'en') {
        const language = window.document.documentElement.getAttribute('lang') || lang;
        const months = [];
        const lists = [
            { value: '01', label: { en: 'January', id: 'Januari' } },
            { value: '02', label: { en: 'February', id: 'Februari' } },
            { value: '03', label: { en: 'March', id: 'Maret' } },
            { value: '04', label: { en: 'April', id: 'April' } },
            { value: '05', label: { en: 'May', id: 'Mei' } },
            { value: '06', label: { en: 'June', id: 'Juni' } },
            { value: '07', label: { en: 'July', id: 'Juli' } },
            { value: '08', label: { en: 'August', id: 'Agustus' } },
            { value: '09', label: { en: 'September', id: 'September' } },
            { value: '10', label: { en: 'October', id: 'Oktober' } },
            { value: '11', label: { en: 'November', id: 'November' } },
            { value: '12', label: { en: 'December', id: 'Desember' } }
        ];

        Array.prototype.filter.call(lists, list=>{
            months.push({ value : list.value, label : list.label[language]});
        });

        return months;
    },
    days() {
        const days = [];
        for (let i = 1; i <= 31; i++) {
            const dayValue = i < 10 ? `0${i}` : `${i}`; // Add leading zero if necessary
            days.push({ value: dayValue, label: dayValue });
        }
        return days;
    },
    hours() {
        const hours = [];
        for (let i = 0; i <= 23; i++) {
            const dayValue = i < 10 ? `0${i}` : `${i}`; // Add leading zero if necessary
            hours.push({ value: dayValue, label: dayValue });
        }
        return hours;
    },
    minutes() {
        const minutes = [];
        for (let i = 0; i <= 59; i++) {
            const dayValue = i < 10 ? `0${i}` : `${i}`; // Add leading zero if necessary
            minutes.push({ value: dayValue, label: dayValue });
        }
        return minutes;
    },
    dayNames(lang = 'en') {
        const language = window.document.documentElement.getAttribute('lang') || lang;
        const englishWeekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        const indonesianWeekdays = ['Minggu', 'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu'];
        if (language === 'id') {
            return indonesianWeekdays;
        } else {
            return englishWeekdays;
        }
    },
    shortDayNames(lang = 'en') {
        const language = window.document.documentElement.getAttribute('lang') || lang;
        const englishWeekdaysShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        const indonesianWeekdaysShort = ['Min', 'Sen', 'Sel', 'Rab', 'Kam', 'Jum', 'Sab'];
        if (language === 'id') {
            return indonesianWeekdaysShort;
        } else {
            return englishWeekdaysShort;
        }
    },
    isDate(dateString) {
        // Regular expression for the date format "yyyy-mm-dd"
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;

        if (!dateRegex.test(dateString)) {
            return false;
        }

        // Parse the date
        const [year, month, day] = dateString.split('-').map(Number);

        // Check if the date is valid
        const date = new Date(year, month - 1, day);
        return date.getFullYear() === year &&
            date.getMonth() === month - 1 &&
            date.getDate() === day;
    },
    isDateTime(dateTimeString) {
        // Regular expression for the date and time format "yyyy-mm-dd h:i:s"
        const dateTimeRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/;

        if (!dateTimeRegex.test(dateTimeString)) {
            return false;
        }

        // Split the string into date and time parts
        const [datePart, timePart] = dateTimeString.split(' ');
        const [year, month, day] = datePart.split('-').map(Number);
        const [hours, minutes, seconds] = timePart.split(':').map(Number);

        // Check if the date is valid
        const date = new Date(year, month - 1, day, hours, minutes, seconds);
        return date.getFullYear() === year &&
            date.getMonth() === month - 1 &&
            date.getDate() === day &&
            date.getHours() === hours &&
            date.getMinutes() === minutes &&
            date.getSeconds() === seconds;
    },
    dateParse(date) {
        if (!date) {
            return null;
        }

        // If it's already a Date object, return it.
        if (date instanceof Date) {
            return moment(date);
        }

        // If it's a number (timestamp), convert it.
        if (typeof date === 'number') {
            const m = moment(date);
            return m.isValid() ? m : null;
        }

        // If it's not a string, we can't parse it.
        if (typeof date !== 'string') {
            return null;
        }

        // Try parsing with a list of common formats.
        const formats = [
            moment.ISO_8601, // Handles 'YYYY-MM-DDTHH:mm:ss.SSSZ' and 'YYYY-MM-DDTHH:mm:ss'
            'YYYY-MM-DD HH:mm:ss',
            'YYYY-MM-DD',
            'MM/DD/YYYY',
            'DD/MM/YYYY',
            'YYYY/MM/DD',
        ];

        for (const format of formats) {
            const m = moment(date, format, false); // Use strict parsing
            if (m.isValid()) {
                return m;
            }
        }

        // As a fallback, try Moment's flexible parsing.
        const fallbackMoment = moment(date);
        if (fallbackMoment.isValid()) {
            return fallbackMoment;
        }

        return null; // Return null if no format matches.
    },
    weekDays(locale = 'en-US') {
        const formatter = new Intl.DateTimeFormat(locale, { weekday: 'long' });
        const options = [];
        Array.from({ length: 7 }, (_, i) => {
            const date = new Date(2021, 0, i + 3);
            return formatter.format(date);
        }).forEach((day, index) => {
            options.push({
                label: day,
                value: (index + 1).toString()
            });
        });
        return options;
    }
}
